<template>
  <Panel :header="t('Estatísticas')" data-testid="agent-stats">
    <Message
      v-if="error"
      severity="error"
      :closable="false"
      data-testid="agent-stats-error"
    >
      {{ error }}
    </Message>

    <ProgressSpinner
      v-else-if="loading && stats === null"
      style="width: 2rem; height: 2rem"
      data-testid="agent-stats-loading"
    />

    <template v-else-if="stats">
      <dl class="assistant-config-stats-grid">
        <div class="assistant-config-stat">
          <dt>{{ t('Conversas') }}</dt>
          <dd data-testid="agent-stats-chat-count">{{ stats.chatCount }}</dd>
        </div>
        <div class="assistant-config-stat">
          <dt>{{ t('Mensagens') }}</dt>
          <dd data-testid="agent-stats-message-count">{{ stats.messageCount }}</dd>
        </div>
        <div class="assistant-config-stat">
          <dt>{{ t('Usuários') }}</dt>
          <dd data-testid="agent-stats-user-count">{{ stats.userCount }}</dd>
        </div>
        <div class="assistant-config-stat">
          <dt>{{ t('Latência média (ms)') }}</dt>
          <dd data-testid="agent-stats-avg-latency">
            {{ formatLatency(stats.avgAssistantLatencyMs) }}
          </dd>
        </div>
      </dl>

      <div v-if="stats.toolOutcomes.length > 0" class="assistant-config-tool-outcomes">
        <h4 class="assistant-config-subtitle">{{ t('Ferramentas (ok / erro)') }}</h4>
        <ul class="assistant-config-tool-list">
          <li
            v-for="(tool, index) in stats.toolOutcomes"
            :key="tool.name"
            class="assistant-config-tool"
            :data-testid="`agent-stats-tool-${index}`"
          >
            <span class="assistant-config-tool-name">{{ tool.name }}</span>
            <span class="assistant-config-tool-ok">{{ tool.ok }}</span>
            <span class="assistant-config-tool-sep">/</span>
            <span class="assistant-config-tool-error">{{ tool.error }}</span>
          </li>
        </ul>
      </div>
    </template>
  </Panel>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAgentStats } from '../../composables/useAgentStats';

const { t } = useI18n();

const { stats, loading, error, load } = useAgentStats();

function formatLatency(value: number | null): string {
  return value === null ? '—' : String(Math.round(value));
}

onMounted(async () => {
  try {
    await load();
  } catch {
    // The composable already set `error`; matches the original allSettled
    // behaviour so a load rejection never propagates as unhandled.
  }
});
</script>

<style scoped>
.assistant-config-stats-grid {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 0.75rem;
}

.assistant-config-stat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assistant-config-stat dt {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  margin: 0;
}

.assistant-config-stat dd {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.assistant-config-subtitle {
  margin: 1rem 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.assistant-config-tool-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assistant-config-tool {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.assistant-config-tool-name {
  font-family: monospace;
}

.assistant-config-tool-ok {
  color: var(--app-success-color);
  font-weight: 700;
}

.assistant-config-tool-error {
  color: var(--app-danger-color);
  font-weight: 700;
}

.assistant-config-tool-sep {
  color: var(--p-text-muted-color);
}
</style>
